/**
 * Shared WordPress REST API helpers for the rennzonenews.com blog source.
 * Used by both BlogIndexPage and BlogPostPage ducks (SSR via Node fetch,
 * browser via window.fetch).
 */

export const WP_API_BASE = 'https://rennzonenews.com/wp-json/wp/v2';

const DEFAULT_TIMEOUT_MS = 8000;

const fetchJson = (url) => {
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS)
    : null;

  return fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: controller ? controller.signal : undefined,
  })
    .then((res) => {
      if (!res.ok) {
        throw Object.assign(new Error(`WP API ${res.status}`), {
          status: res.status,
          url,
        });
      }
      const totalHeader = res.headers.get('x-wp-total');
      const totalPagesHeader = res.headers.get('x-wp-totalpages');
      return res.json().then((data) => ({
        data,
        total: totalHeader ? parseInt(totalHeader, 10) : null,
        totalPages: totalPagesHeader ? parseInt(totalPagesHeader, 10) : null,
      }));
    })
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
};

/**
 * Map a raw WP post to the slim shape rendered by the blog UI.
 * Pulls embedded data (featured image, author, terms) up to top level
 * so the client doesn't have to dig through `_embedded`.
 */
export const normalizePost = (post) => {
  if (!post) return null;
  const embedded = post._embedded || {};
  const media = (embedded['wp:featuredmedia'] || [])[0];
  const author = (embedded.author || [])[0];
  const termGroups = embedded['wp:term'] || [];
  const categories = [];
  const tags = [];
  termGroups.forEach((group) => {
    (group || []).forEach((t) => {
      if (t.taxonomy === 'category') {
        categories.push({ name: t.name, slug: t.slug });
      } else if (t.taxonomy === 'post_tag') {
        tags.push({ name: t.name, slug: t.slug });
      }
    });
  });

  const sizes = media?.media_details?.sizes || {};
  const featuredImage = media
    ? {
        source: sizes.medium_large?.source_url || media.source_url,
        full: sizes.full?.source_url || media.source_url,
        thumbnail: sizes.thumbnail?.source_url || media.source_url,
        alt: media.alt_text || post.title?.rendered || '',
        width: media.media_details?.width,
        height: media.media_details?.height,
      }
    : null;

  return {
    id: post.id,
    slug: post.slug,
    title: post.title?.rendered || '',
    date: post.date,
    modified: post.modified,
    excerptHtml: post.excerpt?.rendered || '',
    contentHtml: post.content?.rendered || '',
    featuredImage,
    author: author ? { name: author.name, slug: author.slug } : null,
    categories,
    tags,
    link: post.link,
  };
};

export const fetchPostList = ({ page = 1, perPage = 20 } = {}) => {
  const url = `${WP_API_BASE}/posts?_embed=true&per_page=${perPage}&page=${page}`;
  return fetchJson(url).then(({ data, total, totalPages }) => ({
    posts: (data || []).map(normalizePost).filter(Boolean),
    total,
    totalPages,
    page,
    perPage,
  }));
};

export const fetchPostBySlug = (slug) => {
  if (!slug) return Promise.resolve(null);
  const url = `${WP_API_BASE}/posts?_embed=true&slug=${encodeURIComponent(
    slug
  )}`;
  return fetchJson(url).then(({ data }) => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return normalizePost(data[0]);
  });
};

/**
 * Fetch a small set of related posts. Best-effort — used by BlogPostPage to
 * populate the sidebar. Filters out the current post id.
 */
export const fetchRelatedPosts = ({ excludeId, tagSlugs = [], limit = 4 } = {}) => {
  // Try tag-based first; fall back to recent posts if no tags.
  const tagParam =
    tagSlugs && tagSlugs.length > 0
      ? `&tags_exclude=&search=${encodeURIComponent(tagSlugs[0])}`
      : '';
  const url = `${WP_API_BASE}/posts?_embed=true&per_page=${
    limit + 1
  }&orderby=date&order=desc${tagParam}`;
  return fetchJson(url)
    .then(({ data }) =>
      (data || [])
        .map(normalizePost)
        .filter(Boolean)
        .filter((p) => p.id !== excludeId)
        .slice(0, limit)
    )
    .catch(() => []);
};
